/** «PLANȘELE S-AU SCHIMBAT DE LA IMPORT»: arhitectura importată a ieșit din
 *  planșe care nu mai sunt în dosar, sau după import s-a urcat un plan de
 *  nivel nou (`SolutieRand.planse`, socotit de server în
 *  `control-plane/services/planse_import.py`). Arhitectura e imutabilă, deci
 *  structura pornită pe ea ar lucra pe o clădire pe care dosarul n-o mai
 *  descrie.
 *
 *  Nimic nu se reimportă singur. Reimportul citește planșele de acum într-o
 *  arhitectură NOUĂ, care devine ★, iar structura de până atunci rămâne pe cea
 *  veche — e hotărârea omului. Aici doar se spune, cu butonul la îndemână, și
 *  se întreabă înaintea unei porniri pe arhitectura veche
 *  (`confirmaPeArhitecturaVeche`), oricare ar fi drumul pornirii. */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError, startJob } from "../api";
import { t, useT } from "../i18n";
import { useRolProiect } from "../components/rol";
import type { PlanseImport, SolutieRand } from "./api";
import { useRegistruOptional } from "./Registru";

/** Planșele rândului, dacă s-au schimbat de la import; altfel `null`. */
export const planseSchimbate = (rand: SolutieRand | null | undefined): PlanseImport | null =>
  rand?.planse?.schimbate ? rand.planse : null;

/** Ce s-a schimbat, într-o frază: numele fișierelor, nu numărul lor — omul
 *  recunoaște «PARTER.dwg», nu «1 planșă». */
export function ceSAschimbat(p: PlanseImport): string {
  const parti: string[] = [];
  if (p.lipsa.length) {
    parti.push(t("planse.ce_sterse", { fisiere: p.lipsa.map((x) => x.fisier ?? x.fisier_id).join(", ") }));
  }
  if (p.noi.length) {
    parti.push(t("planse.ce_noi", { fisiere: p.noi.map((x) => x.fisier).join(", ") }));
  }
  return parti.join("; ");
}

/** Întrebarea dinaintea unei porniri de structură pe arhitectura `rand`:
 *  `true` = se pornește. Fără planșe schimbate nu se întreabă nimic. */
export function confirmaPeArhitecturaVeche(rand: SolutieRand | null | undefined): boolean {
  const p = planseSchimbate(rand);
  return !p || window.confirm(t("planse.confirma_pe_vechi", { ce: ceSAschimbat(p) }));
}

/** Reimportul planșelor, după confirmare: un import nou, cu căutarea
 *  structurii după el (ca «Generează structura» pe un dosar fără
 *  arhitectură), apoi pe arhitectură, unde se vede importul cât macină. */
export function useReimport(projectId: string) {
  const navigate = useNavigate();
  const registrul = useRegistruOptional();
  const [ocupat, setOcupat] = useState(false);
  const [eroare, setEroare] = useState<string | null>(null);
  const reimporta = async () => {
    if (!window.confirm(t("planse.confirma_reimport"))) return;
    setOcupat(true);
    setEroare(null);
    try {
      await startJob(projectId, "external_architecture", { cautare_structura: true });
      await registrul?.reciteste();
      navigate(`/p/${projectId}/arhitectura`);
    } catch (e) {
      setEroare(e instanceof ApiError && e.message ? e.message : t("planse.esec_reimport"));
    } finally {
      setOcupat(false);
    }
  };
  return { reimporta, ocupat, eroare };
}

/** Banda de avertisment, cu ce s-a schimbat și «Reimportă planșele». Nimic
 *  când planșele importului sunt toate în dosar. */
export default function PlanseSchimbate({ projectId, rand }: {
  projectId: string; rand: SolutieRand | null | undefined;
}) {
  const tr = useT();
  const { potScrie } = useRolProiect();
  const { reimporta, ocupat, eroare } = useReimport(projectId);
  const p = planseSchimbate(rand);
  if (!p) return null;
  return (
    <div className="planse-schimbate" role="alert">
      <b>{tr("planse.titlu")}</b>
      <p>{tr("planse.text")}</p>
      <ul>
        {p.lipsa.map((x) => (
          <li key={x.fisier_id}>
            {tr("planse.stearsa", { fisier: x.fisier ?? x.fisier_id, niveluri: x.niveluri.join(", ") })}
          </li>
        ))}
        {p.noi.map((x) => <li key={x.fisier_id}>{tr("planse.noua", { fisier: x.fisier })}</li>)}
      </ul>
      {potScrie && (
        <button className="btn" disabled={ocupat} onClick={() => void reimporta()}
                title={tr("planse.reimporta_titlu")}>
          {tr(ocupat ? "planse.se_reimporta" : "planse.reimporta")}
        </button>
      )}
      {eroare && <p className="bad">{eroare}</p>}
    </div>
  );
}
